import { MeasureDataSplitDetails, MeasureDataTableRow, WideCell, WideRow } from "store/types";
import { currencySign } from "../../../../constants";

export function formatCellValue(row: MeasureDataTableRow): string {
  const raw = row.value ?? "";
  const num = Number(raw);
  if (isNaN(num)) return raw;
  const formatted = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (row.postfix === "%") return `${formatted}%`;
  if (row.is_currency && row.currency) return `${currencySign[row.currency]}${formatted}`;
  if (row.postfix) return `${row.postfix}${formatted}`;
  return formatted;
}

/**
 * Pivots flat monitoring rows (showTopInvestors: true) into wide rows.
 *
 * Each top-level row = one measure for a date/round (aggregated).
 * Its `.children` = per-investor splits for that measure.
 *
 * Wide row = one (date, round) group.
 *   Parent row  → aggregated values from the top-level rows.
 *   Child rows  → one per investor, values collected across all measures.
 */
export function buildWideData(topLevelRows: MeasureDataTableRow[]): {
  wideRows: WideRow[];
  measureColumns: string[];
} {
  // Collect all measure names preserving first-seen order (top-level + children)
  const measureOrder: string[] = [];
  const measureSeen = new Set<string>();
  const addMeasure = (name: string) => {
    if (!measureSeen.has(name)) { measureSeen.add(name); measureOrder.push(name); }
  };
  topLevelRows.forEach((r) => {
    addMeasure(r.parameter);
    r.children?.forEach((c) => addMeasure(c.parameter));
  });

  // Group top-level rows by (date, round)
  const groupMap = new Map<string, MeasureDataTableRow[]>();
  topLevelRows.forEach((row) => {
    const dateKey = row.date ? row.date.slice(0, 10) : "";
    const groupKey = `${dateKey}||${row.round ?? ""}`;
    if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
    groupMap.get(groupKey)!.push(row);
  });

  const wideRows: WideRow[] = [];

  groupMap.forEach((groupRows, groupKey) => {
    const firstRow = groupRows[0];
    const date = firstRow.date ? firstRow.date.slice(0, 10) : "";
    const round = firstRow.round ?? "—";

    const anyHasChildren = groupRows.some((r) => r.children && r.children.length > 0);

    if (anyHasChildren) {
      // ── Rows have investor children (showTopInvestors: true response) ────────
      //
      // Build:
      //   parentCells  → aggregated values from top-level rows (pre-summed by API)
      //   investorMap  → per-investor cells collected across all measures' children

      const parentCells: Record<string, WideCell | null> = {};
      const allDataIds: number[] = [];
      const allBatchIds = new Set<number>();

      type InvAccum = { cells: Record<string, WideCell | null>; dataIds: number[]; batchIds: Set<number> };
      const investorMap = new Map<string, InvAccum>();

      groupRows.forEach((row) => {
        parentCells[row.parameter] = { displayValue: formatCellValue(row), dataId: row.id };
        allDataIds.push(row.id);
        if (row.batch_id) allBatchIds.add(row.batch_id);

        row.children?.forEach((child) => {
          const invName = child.investor || "—";
          if (!investorMap.has(invName)) {
            investorMap.set(invName, { cells: {}, dataIds: [], batchIds: new Set() });
          }
          const acc = investorMap.get(invName)!;
          acc.cells[row.parameter] = { displayValue: formatCellValue(child), dataId: child.id };
          acc.dataIds.push(child.id);
          allDataIds.push(child.id);
          if (child.batch_id) { acc.batchIds.add(child.batch_id); allBatchIds.add(child.batch_id); }
        });
      });

      const children: WideRow[] = [];
      investorMap.forEach((acc, invName) => {
        children.push({
          key: `${groupKey}_${invName}`,
          batchIds: Array.from(acc.batchIds),
          date,
          round,
          investor: invName !== "—" ? invName : null,
          bankAccount: null,
          cells: acc.cells,
          children: [],
          allDataIds: acc.dataIds,
          isAggregated: false
        });
      });

      wideRows.push({
        key: `${groupKey}_agg`,
        batchIds: Array.from(allBatchIds),
        date,
        round,
        investor: null,
        bankAccount: null,
        cells: parentCells,
        children,
        allDataIds,
        isAggregated: true
      });
    } else {
      // ── Flat rows (no investor children) ─────────────────────────────────────
      //
      // Group by investor field to handle multiple investors in the same group.

      const investorMap = new Map<string, MeasureDataTableRow[]>();
      groupRows.forEach((row) => {
        const key = row.investor || "—";
        if (!investorMap.has(key)) investorMap.set(key, []);
        investorMap.get(key)!.push(row);
      });

      const investorNames = Array.from(investorMap.keys());

      if (investorNames.length === 1) {
        const invRows = investorMap.get(investorNames[0])!;
        const cells: Record<string, WideCell | null> = {};
        const batchIds = new Set<number>();
        invRows.forEach((r) => {
          cells[r.parameter] = { displayValue: formatCellValue(r), dataId: r.id };
          if (r.batch_id) batchIds.add(r.batch_id);
        });
        const investor = investorNames[0] !== "—" ? investorNames[0] : null;
        wideRows.push({
          key: `${groupKey}_${investorNames[0]}`,
          batchIds: Array.from(batchIds),
          date,
          round,
          investor,
          bankAccount: null,
          cells,
          children: [],
          allDataIds: invRows.map((r) => r.id),
          isAggregated: false
        });
      } else {
        // Multiple investors in flat data — build parent + children
        const children: WideRow[] = [];
        const allDataIds: number[] = [];
        const allBatchIds = new Set<number>();
        const parentCellsRaw: Record<string, { sum: number; sampleRow: MeasureDataTableRow }> = {};

        investorMap.forEach((invRows, invName) => {
          const cells: Record<string, WideCell | null> = {};
          const invBatchIds = new Set<number>();
          invRows.forEach((r) => {
            cells[r.parameter] = { displayValue: formatCellValue(r), dataId: r.id };
            allDataIds.push(r.id);
            if (r.batch_id) { allBatchIds.add(r.batch_id); invBatchIds.add(r.batch_id); }
            if (!parentCellsRaw[r.parameter]) {
              parentCellsRaw[r.parameter] = { sum: 0, sampleRow: r };
            }
            parentCellsRaw[r.parameter].sum += Number(r.value ?? 0);
          });
          children.push({
            key: `${groupKey}_${invName}`,
            batchIds: Array.from(invBatchIds),
            date,
            round,
            investor: invName !== "—" ? invName : null,
            bankAccount: null,
            cells,
            children: [],
            allDataIds: invRows.map((r) => r.id),
            isAggregated: false
          });
        });

        const parentCells: Record<string, WideCell | null> = {};
        Object.entries(parentCellsRaw).forEach(([measure, { sum, sampleRow }]) => {
          parentCells[measure] = {
            displayValue: formatCellValue({ ...sampleRow, value: sum.toString() }),
            dataId: -1
          };
        });

        wideRows.push({
          key: `${groupKey}_agg`,
          batchIds: Array.from(allBatchIds),
          date,
          round,
          investor: null,
          bankAccount: null,
          cells: parentCells,
          children,
          allDataIds,
          isAggregated: true
        });
      }
    }
  });

  wideRows.sort((a, b) => b.date.localeCompare(a.date));

  return { wideRows, measureColumns: measureOrder };
}

/**
 * Merge split details from multiple batches into one object for GroupsForm.
 * ui_group_id is remapped to be globally unique so each measure gets its own group.
 */
export function mergeSplitDetails(details: MeasureDataSplitDetails[]): MeasureDataSplitDetails {
  if (details.length <= 1) return details[0] ?? { deal_rounds: {}, splits: {} };

  const merged: MeasureDataSplitDetails = { deal_rounds: {}, splits: {} };
  let nextGroupId = 1;

  details.forEach((detail) => {
    // Merge deal_rounds — keep first occurrence per deal
    Object.entries(detail.deal_rounds).forEach(([dealId, rounds]) => {
      const key = Number(dealId);
      if (!merged.deal_rounds[key]) merged.deal_rounds[key] = rounds;
    });

    // Add each split group with a unique ui_group_id
    Object.values(detail.splits).forEach((splitsArray) => {
      const groupId = nextGroupId++;
      const recordKey = splitsArray[0]?.id ?? groupId;
      merged.splits[recordKey] = splitsArray.map((s) => ({ ...s, ui_group_id: groupId }));
    });
  });

  return merged;
}

/**
 * Keep only split groups where at least one monitoring_data entry has an ID in `dataIds`.
 * Used to narrow the edit drawer to a single clicked measure column.
 */
export function filterSplitDetailsByDataIds(
  splitDetails: MeasureDataSplitDetails,
  dataIds: Set<number>
): MeasureDataSplitDetails {
  const filtered: MeasureDataSplitDetails = {
    deal_rounds: splitDetails.deal_rounds,
    splits: {}
  };
  Object.entries(splitDetails.splits).forEach(([key, splits]) => {
    const matching = splits.filter((split) =>
      split.monitoring_data.some((item) => dataIds.has(item.id))
    );
    if (matching.length > 0) filtered.splits[Number(key)] = matching;
  });
  return filtered;
}

export function filterSplitDetailsByInvestor(
  splitDetails: MeasureDataSplitDetails,
  investorName: string
): MeasureDataSplitDetails {
  const filtered: MeasureDataSplitDetails = {
    deal_rounds: splitDetails.deal_rounds,
    splits: {}
  };
  Object.entries(splitDetails.splits).forEach(([key, splits]) => {
    const filteredSplits = splits
      .map((split) => ({
        ...split,
        monitoring_data: split.monitoring_data.filter(
          (item) => item.investor?.name === investorName
        )
      }))
      .filter((split) => split.monitoring_data.length > 0);
    if (filteredSplits.length > 0) {
      filtered.splits[Number(key)] = filteredSplits;
    }
  });
  return filtered;
}
